import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import UserModel from "../../models/user";
import { DataTable } from "../../components";
import { UserInterface } from "../../interfaces";
import sendWelcomeEmail from './welcomeEmail';

const ViewUsers = () => {
  const [users, setUsers] = useState<UserInterface[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15 });
  const [isLoading, setIsLoading] = useState(false);



  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserInterface | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [sendEmail, setSendEmail] = useState(true);

  const fetchUsers = async (pageIndex: number = 0, pageSize: number = 10) => {
    try {
      setIsLoading(true);
      const skip = pageIndex * pageSize;

      const resp = await UserModel.fetchAll<any>({
        $skip: skip,
        $limit: pageSize,
        $sort: { createdAt: -1 },
      });

      return {
        rows: resp.data.filter((item: any) => item.email),
        totalCount: resp.total,
      };
    } catch (error: any) {
      throw new Error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = (user: UserInterface) => {
    setSelectedUser(user);
    setEmail(user.email);
    setRole(user.role || "user");
    setPassword("");
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setSelectedUser(null);
    setEmail("");
    setPassword("");
    setSendEmail(true);
    setIsModalVisible(false);
  };

  const sendCreationEmail = async () => {
    if (sendEmail) {
      toast.promise(sendWelcomeEmail(email, password), {
        loading: "Sending Welcome Email...",
        success: <b>Send Successful!</b>,
        error: <b>Send Failed</b>,
      });
    }
  };

  const createUser = async () => {
    try {
      const newUser = { email, password, role };
      await UserModel.create(newUser);
      await sendCreationEmail();
      toast.success("User created successfully!");
      handleModalClose();
      fetchUsers();
    } catch (error: any) {
      toast.error(`Failed to create user: ${error.message}`);
    }
  };

  const updateUser = async () => {
    if (!selectedUser?._id) return;

    try {
      const updatedUser = { email, role, ...(password && { password }) }; // Include password only if it's not empty
      await UserModel.update(selectedUser._id, updatedUser);
      toast.success("User updated successfully!");
      handleModalClose();
      fetchUsers();
    } catch (error: any) {
      toast.error(`Failed to update user: ${error.message}`);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser?._id) return;

    try {
      await UserModel.delete(selectedUser._id);
      toast.success("User deleted successfully!");
      handleModalClose();
      fetchUsers();
    } catch (error: any) {
      toast.error(`Failed to delete user: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchUsers(pagination.pageIndex, pagination.pageSize).then((res) => {
      setUsers(res.rows);
      setTotalCount(res.totalCount);
    });
  }, [pagination]);

  return (
    <div>
      <DataTable
        columns={UserModel.getColumns()}
        onRowClick={(row) => handleRowClick(row.original)}
        data={users}
        pagination={pagination}
        onPaginationChange={setPagination}
        totalRowCount={totalCount}
        loading={isLoading}
      />
      <div className="d-flex justify-content-end mb-3 p-3">
        <Button variant="primary" onClick={() => setIsModalVisible(true)}>
          Create New User
        </Button>
      </div>

      <Modal show={isModalVisible} onHide={() => handleModalClose()}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedUser ? "Update User" : "Create New User"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formRole">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
            {!selectedUser && (
              <Form.Group className="mb-3" controlId="formSendEmail">
                <Form.Check
                  type="switch"
                  label="Send Welcome Email"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {selectedUser && (
            <Button
              variant="danger"
              onClick={deleteUser}
              className="me-auto"
            >
              Delete User
            </Button>
          )}
          <Button variant="secondary" onClick={() => handleModalClose()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={selectedUser ? updateUser : createUser}
          >
            {selectedUser ? "Update User" : "Create User"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViewUsers;
